import { useEffect } from 'react';
import { Box, Card, FormField, FormHelp, Heading, Input, Label, Paragraph, Text } from '@zillow/constellation-10';
import { constant, get, range, take, times } from 'lodash-es';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { MAX_OCCUPANT_FULL_NAME_LENGTH } from '../../../../constants/form';
import { validateDateOfBirth } from '../../../../constants/validation';
import { FIELD_IDS, STEP_IDS } from '../../../../constants/wizard';
import { getApplicantName } from '@zg-rentals/applications-utils';
import type { AdditionalOccupant } from '../../../../models/rental-application';
import { getIsRenterOccupant, getOccupantCount } from '../../../../utils/additionalOccupants';
import { pluralize, today } from '../../../../utils/formatters';
import { DatePicker } from '../../../DatePicker';
import { EditInStepLink } from './EditInStepLink';
import type { SupplementalSectionProps } from './supplementalSections';

const ADDITIONAL_OCCUPANTS_FIELD_ARRAY_NAME = 'additionalOccupants';
const MIN_DATE_OF_BIRTH = new Date(1900, 0, 1);

const emptyAdditionalOccupant: AdditionalOccupant = { fullName: null, dateOfBirth: null };

export function AdditionalOccupantsSection({ applicationId, answers, onPersist }: SupplementalSectionProps) {
  const { watch } = useFormContext();
  const occupants = watch(FIELD_IDS.OCCUPANTS) ?? answers.occupants;
  const occupantCount = getOccupantCount(occupants);
  const { fields, append, remove } = useFieldArray({ name: ADDITIONAL_OCCUPANTS_FIELD_ARRAY_NAME });
  const renterFullName = getApplicantName(answers);

  useEffect(() => {
    if (fields.length < occupantCount) {
      append(times(occupantCount - fields.length, constant(emptyAdditionalOccupant)), { shouldFocus: false });
    } else if (fields.length > occupantCount) {
      remove(range(occupantCount, fields.length));
    }
  }, [occupantCount, fields.length, append, remove]);

  return (
    <>
      <Heading level={4} css={{ textStyle: 'body-bold', color: 'text.subtle' }}>
        Occupants
      </Heading>
      <Paragraph>
        You listed <strong>{pluralize('occupant', occupantCount)}</strong>, including yourself.{' '}
        <EditInStepLink
          applicationId={applicationId}
          stepId={STEP_IDS.OCCUPANTS}
          label="Household"
          onPersist={onPersist}
        />
      </Paragraph>
      <Box css={{ display: 'flex', flexDirection: 'column', gap: 'default', marginTop: 'default' }}>
        {take(fields, occupantCount).map((additionalOccupant, index) => (
          <AdditionalOccupantEdit key={additionalOccupant.id} index={index} renterFullName={renterFullName} />
        ))}
      </Box>
    </>
  );
}

function AdditionalOccupantEdit({ index, renterFullName }: { index: number; renterFullName: string }) {
  const {
    register,
    formState: { errors },
  } = useFormContext();

  const isRenterOccupant = getIsRenterOccupant(index);
  const title = isRenterOccupant ? `Occupant #${index + 1} (you)` : `Occupant #${index + 1}`;
  const fullNameFieldName = `${ADDITIONAL_OCCUPANTS_FIELD_ARRAY_NAME}[${index}].fullName`;
  const dateOfBirthFieldName = `${ADDITIONAL_OCCUPANTS_FIELD_ARRAY_NAME}[${index}].dateOfBirth`;
  const dateOfBirthLabelId = `additionalOccupants${index}-dateOfBirth-label`;
  const errorFullName = get(errors, fullNameFieldName);

  return (
    <Card
      elevated={false}
      outlined
      aria-label={title}
      css={{ display: 'flex', flexDirection: 'column', gap: 'default' }}
    >
      <Heading level={5} css={{ textStyle: 'body-bold', color: 'text.subtle' }}>
        {title}
      </Heading>
      <Box>
        {isRenterOccupant ? (
          <Box css={{ display: 'flex', flexDirection: 'column', gap: 'tighter', marginBottom: 'layout.tight' }}>
            <Text textStyle="body-sm-bold">Full name</Text>
            <Text>{renterFullName}</Text>
          </Box>
        ) : (
          <FormField
            label={<Label>Full name</Label>}
            optional
            control={<Input maxLength={MAX_OCCUPANT_FULL_NAME_LENGTH} {...register(fullNameFieldName)} />}
            description={errorFullName?.message ? <FormHelp>{String(errorFullName.message)}</FormHelp> : undefined}
            error={!!errorFullName}
          />
        )}
        <Controller
          name={dateOfBirthFieldName}
          rules={{ validate: validateDateOfBirth }}
          render={({ field, fieldState: { error, invalid } }) => (
            <FormField
              label={<Label id={dateOfBirthLabelId}>Date of birth</Label>}
              optional
              description={error?.message ? <FormHelp>{error.message}</FormHelp> : undefined}
              error={invalid}
            >
              <DatePicker
                {...field}
                value={field.value ?? ''}
                minDate={MIN_DATE_OF_BIRTH}
                maxDate={today()}
                aria-labelledby={dateOfBirthLabelId}
              />
            </FormField>
          )}
        />
      </Box>
    </Card>
  );
}
